<template>
    <eSesjaModal>
        <eSesjaModalHeader @close="closeModal">
            {{ $t("com.global.add") }} {{ $t("com.discussion.profile.modes") }}
        </eSesjaModalHeader>
        <eSesjaModalBody>
            <section class="discussion-profile-mode-form" v-if="newMode && availableTypesForAdd.length">
                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-type") }}</label>
                    <select v-model.number="newMode.typeId" class="edit-point-form__input" @change="syncNewTypeName">
                        <option v-for="type in availableTypesForAdd" :key="type.id" :value="type.id">
                            {{ type.name }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <section class="discussion-profile-mode-form__switch-row">
                        <InputSwitch id="new-mode-block-modal" v-model="newMode.isBlock" />
                        <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-mode-block-modal">
                            {{ $t("com.discussion.profile.mode-block") }}
                        </label>
                    </section>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-first-statement") }}</label>
                    <select v-model.number="newMode.firstStatement" class="edit-point-form__input" :disabled="newMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`new-first-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-second-statement") }}</label>
                    <select v-model.number="newMode.secondStatement" class="edit-point-form__input" :disabled="newMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`new-second-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-third-statement") }}</label>
                    <select v-model.number="newMode.thirdStatement" class="edit-point-form__input" :disabled="newMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`new-third-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-next-statement") }}</label>
                    <select v-model.number="newMode.nextStatement" class="edit-point-form__input" :disabled="newMode.isBlock">
                        <option v-for="option in statementTimeOptions" :key="`new-next-${option.id}`" :value="option.id">
                            {{ $t(option.textKey) }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <section class="discussion-profile-mode-form__switch-row">
                        <InputSwitch id="new-mode-turn-off-modal" v-model="newMode.turnOff" :disabled="newMode.isBlock" />
                        <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-mode-turn-off-modal">
                            {{ $t("com.discussion.profile.mode-turn-off") }}
                        </label>
                    </section>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <section class="discussion-profile-mode-form__switch-row">
                        <InputSwitch id="new-mode-sound-modal" v-model="newMode.isSoundNotification" :disabled="newMode.isBlock" />
                        <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-mode-sound-modal">
                            {{ $t("com.discussion.profile.mode-sound-notification") }}
                        </label>
                    </section>
                </section>
            </section>
            <section v-else class="text-center">
                {{ $t("com.global.no-data") }}
            </section>
        </eSesjaModalBody>
        <eSesjaModalFooter>
            <eSesjaModalButtonCancel @close="closeModal">
                {{ $t("com.global.cancel") }}
            </eSesjaModalButtonCancel>
            <eSesjaModalButtonSave :disabled="isSaveDisabled" @save="createMode">
                {{ $t("com.global.save") }}
            </eSesjaModalButtonSave>
        </eSesjaModalFooter>
    </eSesjaModal>
</template>

<script charset="utf-8">
import AlertHelper from "@/helpers/alerts.helper.js";
import DiscussionProfileService from "@/services/discussion.profile.service";
import InputSwitch from "@/components/BasicControls/InputSwitch.vue";

export default {
    name: "AddDiscussionProfileModeModal",
    components: {
        InputSwitch
    },
    props: {
        discussionId: {
            type: Number,
            required: true
        },
        statementTypes: {
            type: Array,
            default: () => []
        },
        statementTimeOptions: {
            type: Array,
            default: () => []
        },
        usedTypeIds: {
            type: Array,
            default: () => []
        }
    },
    emits: ["close", "created"],
    data() {
        return {
            isSaving: false,
            newMode: null
        };
    },
    computed: {
        availableTypesForAdd() {
            return this.statementTypes.filter((type) => !this.usedTypeIds.includes(type.id));
        },
        isSaveDisabled() {
            return this.isSaving || !this.newMode || this.newMode.typeId <= 0;
        }
    },
    mounted() {
        this.resetForm();
    },
    methods: {
        createDefaultModeModel() {
            const firstAvailableType = this.availableTypesForAdd[0];

            return {
                type: firstAvailableType?.name || "",
                typeId: firstAvailableType?.id || 0,
                isBlock: false,
                firstStatement: 60,
                secondStatement: 60,
                thirdStatement: 60,
                nextStatement: 60,
                turnOff: false,
                isSoundNotification: false
            };
        },
        resetForm() {
            this.newMode = this.createDefaultModeModel();
        },
        syncNewTypeName() {
            if (!this.newMode) {
                return;
            }

            const selectedType = this.statementTypes.find((type) => type.id === this.newMode.typeId);
            this.newMode.type = selectedType?.name || "";
        },
        normalizeNumber(value) {
            const parsed = Number(value);

            if (!Number.isFinite(parsed)) {
                return 0;
            }

            return parsed;
        },
        closeModal() {
            this.$emit("close");
        },
        async createMode() {
            if (this.isSaveDisabled) {
                return;
            }

            try {
                this.isSaving = true;

                await DiscussionProfileService.AddMode(this.discussionId, {
                    type: this.newMode.type,
                    typeId: this.newMode.typeId,
                    isBlock: this.newMode.isBlock,
                    firstStatement: this.normalizeNumber(this.newMode.firstStatement),
                    secondStatement: this.normalizeNumber(this.newMode.secondStatement),
                    thirdStatement: this.normalizeNumber(this.newMode.thirdStatement),
                    nextStatement: this.normalizeNumber(this.newMode.nextStatement),
                    turnOff: this.newMode.turnOff,
                    isSoundNotification: this.newMode.isSoundNotification
                });

                this.$emit("created");
                this.closeModal();
            } catch (e) {
                if (e.response !== undefined) {
                    const t = this.$swal.mixin(AlertHelper.errorToastElement());
                    t.fire(
                        AlertHelper.errorToastAlert(
                            this.$t("com.global.default-save-error"),
                            this.$t("com.global.error")
                        )
                    );
                }
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.discussion-profile-mode-form {
    display: flex;
    flex-direction: column;
    row-gap: 16px;

    &__row {
        display: flex;
        flex-direction: column;
    }

    &__switch-row {
        display: flex;
        align-items: center;
        column-gap: 12px;
        min-height: 38px;
    }

    &__switch-label {
        margin: 0;
    }
}

.edit-point-form__input {
    width: 100%;
    padding: 12.5px 16px;
    background: #ffffff;
    border: 1px solid #d1d6df;
    box-sizing: border-box;
    box-shadow: 1px 1px 3px rgba(0, 8, 44, 0.06);
    border-radius: 4px;
    margin: 8px 0px;
    font-size: 16px;
    line-height: 144%;
    color: #00082c;

    &:focus {
        border: 1px solid #00082c;
    }
}

.edit-point-form__label {
    font-weight: 600;
    font-size: 14px;
    line-height: 145%;
    color: #00082c;
}

.mode-switch {
    display: flex;
    justify-content: center;
}
</style>
